import axios, { AxiosError } from "axios";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { sanitizeEndpoint, Tracker } from "../analytics";

export interface IAuthHttp {
    doGet<TResponse>(path: string): Promise<TResponse>;
    doPost<TBody, TResult>(path: string, body: TBody): Promise<{ result: TResult|null; status: number; }|null>;
    doPut<TBody>(path: string, body: TBody): Promise<TBody>;
    doDelete(path: string): Promise<Response | null>;
    doPatch<TBody, TResult>(path: string, body: TBody): Promise<{ result: TResult|null; status: number; }|null>;
}

const inFlightPosts = new Map<string, Promise<{ result: unknown; status: number; } | null>>();
const insertIdempotencyKeys = new Map<string, string>();

function createIdempotencyKey(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isAssetInsertPath(path: string): boolean {
    return path.replace(/^\//, "").startsWith("asset/insert");
}

function mutationFingerprint(path: string, body: unknown): string {
    return `${path}:${JSON.stringify(body)}`;
}

const useAuthHttp = (): IAuthHttp => {
    const auth = useAuthContext();
    const navigate = useNavigate();

    async function doGet<TResponse>(path: string): Promise<TResponse> {
        try {
            const response = await axios.get<TResponse>(`${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, {
                headers: {
                    Authorization: auth.token
                }
            });

            return {...response.data, status: response.status};
        } catch (err) {
            const axiosError = err as AxiosError;
            const status = axiosError.response?.status ?? 0;
            if (status >= 400) {
                Tracker.trackRequestFailed(sanitizeEndpoint(path), status, 'GET');
            }
            if ((axiosError.response?.status == 502)) {
                navigate("/bad-gateway");
            }

            if (axiosError.response?.status == 401) {
                await auth.logout();
            }

            throw err;
        }
    }

    async function doPost<TBody, TResult>(path: string, body: TBody) {
        return doSomething<TBody, TResult>(path, body, "POST");
    }
    
    async function doPatch<TBody, TResult>(path: string, body: TBody) {
        return doSomething<TBody, TResult>(path, body, "PATCH");
    }

    async function doPut<TBody>(path: string, body: TBody) {
        const headers = await getHeaders();
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method: "PUT",
                body: JSON.stringify(body),
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 500) {
            Tracker.trackRequestFailed(sanitizeEndpoint(path), result.status, 'PUT');
            console.error(result);
            navigate("/bad-gateway");
            return null;
        }

        return await result.json();
    }

    async function doDelete(path: string) {
        const headers = await getHeaders();
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method: "DELETE",
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 500) {
            Tracker.trackRequestFailed(sanitizeEndpoint(path), result.status, 'DELETE');
            console.error(result);
            navigate("/bad-gateway");
            
            return null;
        }

        return result;
    }

    async function doSomething<TBody, TResult>(path: string, body: TBody, method: string): Promise<{ result: TResult|null; status: number; }|null> {
        const fingerprint = mutationFingerprint(path, body);
        if (method === "POST" && isAssetInsertPath(path) && inFlightPosts.has(fingerprint)) {
            return inFlightPosts.get(fingerprint) as Promise<{ result: TResult|null; status: number; }|null>;
        }

        const request = executeMutation<TBody, TResult>(path, body, method, fingerprint);
        if (method === "POST" && isAssetInsertPath(path)) {
            inFlightPosts.set(fingerprint, request as Promise<{ result: unknown; status: number; } | null>);
        }

        try {
            return await request;
        } finally {
            inFlightPosts.delete(fingerprint);
        }
    }

    async function executeMutation<TBody, TResult>(path: string, body: TBody, method: string, fingerprint: string): Promise<{ result: TResult|null; status: number; }|null> {
        const headers = await getHeaders(path, method, fingerprint);
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method,
                body: JSON.stringify(body),
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 400 && result.status < 500) {
            Tracker.trackRequestFailed(sanitizeEndpoint(path), result.status, method);
            throw new Error((await result.json())?.message ?? "Erro ao processar a requisição");
        }

        if (result.status >= 500) {
            Tracker.trackRequestFailed(sanitizeEndpoint(path), result.status, method);
            console.error(result);
            navigate("/bad-gateway");
            
            return null;
        }

        let responseResult;
        try {
            responseResult = await result.json();
        } catch {
            responseResult = null;
        }

        // Mantém a chave em erro de servidor: a escrita pode ter ocorrido e a
        // retentativa precisa cair no mesmo registro de idempotência.
        if (method === "POST" && isAssetInsertPath(path) && result.status < 500) {
            insertIdempotencyKeys.delete(fingerprint);
        }

        return { result: responseResult, status: result.status };
    }

    const getHeaders = async (path?: string, method?: string, fingerprint?: string) => {
        if (!auth.token) {
            await auth.logout();
            return undefined;
        }

        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "Authorization": auth.token 
        };

        if (method === "POST" && path && fingerprint && isAssetInsertPath(path)) {
            if (!insertIdempotencyKeys.has(fingerprint)) {
                insertIdempotencyKeys.set(fingerprint, createIdempotencyKey());
            }
            headers["Idempotency-Key"] = insertIdempotencyKeys.get(fingerprint)!;
        }

        return headers;
    }
    
    return { doGet, doPost, doPut, doDelete, doPatch };
}

export default useAuthHttp;
